import * as React from 'react'
import styled from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import { LocaleMoneyDisplayer } from '@ulule/owl-kit-components'
import { NUMBER_FORMAT_CONFIG } from '@ulule/owl-kit-components/next'
import * as env from '@owl-nest/config'

type MoneyDisplayerProps = {
  readonly amount: number
  readonly currency?: string // FIXME Remove optional trait when removing `project` attribute
  readonly precision?: number
  // @deprecated: Pass `currency` instead
  readonly project?: api.Project
}

export function MoneyDisplayer({
  amount,
  currency,
  precision,
  project,
}: MoneyDisplayerProps): React.ReactElement<MoneyDisplayerProps> {
  const projectCurrency = project?.currency // FIXME: Should pass only the currency, not whole project since the component isn't project-bound.
  const userLanguage = env.getUserConfig().lang

  return (
    <LocaleMoneyDisplayer
      amount={amount}
      currency={currency ?? projectCurrency}
      localeFormat={userLanguage.toLowerCase()}
      precision={precision}
      rates={env.CURRENCIES_RATES}
      targetCurrency={currency}
    />
  )
}

type CurrencyDisplayerProps = {
  className?: string
  children: React.ReactNode
  currency: string
  // Wrap currency into a <span> to allow its positioning
  wrapCurrency?: boolean
}

export enum SymbolPosition {
  AHEAD,
  BEHIND,
}

export function CurrencyDisplayer({
  className,
  children,
  currency,
  wrapCurrency = false,
}: CurrencyDisplayerProps): React.ReactElement<CurrencyDisplayerProps> {
  const currencySymbol = env.CURRENCIES_SYMBOL[currency].symbol
  const userConfig = env.useUserEnv()
  const position = getPosition(userConfig.lang)

  if (wrapCurrency) {
    return (
      <>
        {position === SymbolPosition.AHEAD && <WrapCurrency className={className}>{currencySymbol}</WrapCurrency>}
        {children}
        {position === SymbolPosition.BEHIND && <WrapCurrency className={className}>{currencySymbol}</WrapCurrency>}
      </>
    )
  }

  return (
    <>
      {position === SymbolPosition.AHEAD && currencySymbol}
      {children}
      {position === SymbolPosition.BEHIND && currencySymbol}
    </>
  )
}

type WrapCurrencyProps = {
  className?: string
  children: React.ReactNode
}

function WrapCurrency({ className, children }: WrapCurrencyProps): React.ReactElement<WrapCurrencyProps> {
  return <CurrencyWrapper className={className}>{children}</CurrencyWrapper>
}

export const CurrencyWrapper = styled.span``

export function getPosition(userLanguage: api.Lang): SymbolPosition {
  const format = NUMBER_FORMAT_CONFIG[userLanguage].format
  const indexOfSymbol = format.indexOf('%s')
  const indexOfValue = format.indexOf('%v')

  if (indexOfSymbol < indexOfValue) {
    return SymbolPosition.AHEAD
  } else {
    return SymbolPosition.BEHIND
  }
}

export { SecurePaymentModal } from './components/SecurePaymentModal'
